import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import FilterPanel from '../sugar-marketplace-dashboard/components/FilterPanel';
import MarketplaceTable from '../sugar-marketplace-dashboard/components/MarketplaceTable';
import MarketplaceCards from '../sugar-marketplace-dashboard/components/MarketplaceCards';
import SugarBookingModal from '../sugar-marketplace-dashboard/components/SugarBookingModal';
import BundleBookingModal from '../sugar-marketplace-dashboard/components/BundleBookingModal';


const FactoryBuyerMarketplaceView = () => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('table');
  const [filters, setFilters] = useState({
    state: 'all',
    mill: 'all',
    grade: 'all',
    distanceSlab: 'all',
    search: '',
    minPrice: '',
    maxPrice: ''
  });
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  const [availableMills, setAvailableMills] = useState([]);
  
  // Modal states
  const [sugarBookingModal, setSugarBookingModal] = useState({ isOpen: false, data: null });
  const [bundleBookingModal, setBundleBookingModal] = useState({ isOpen: false, data: null, noTrucks: false });

  const mockLoads = [
    {
      id: 1,
      date: '2025-11-28',
      offerId: 'SLO2025001',
      product: 'White Refined Sugar',
      millName: 'Shree Renuka Sugars Ltd',
      millCode: 'MILL001',
      state: 'MH',
      stateName: 'Maharashtra',
      city: 'Kolhapur',
      deliveryFrom: 'Kolhapur, Maharashtra',
      distanceFromBuyer: 250,
      paymentDate: '2025-12-05',
      liftingDate: '2025-12-10',
      grade: 'S-30',
      season: '2024-25',
      packSize: '50 kg bags',
      quantity: 500,
      rate: 38500,
      trucksAvailable: 12,
      insuranceAvailable: true,
      transportationRatePerTon: 1200
    },
    {
      id: 2,
      date: '2025-11-29',
      offerId: 'SLO2025002',
      product: 'White Refined Sugar',
      millName: 'Balrampur Chini Mills',
      millCode: 'MILL002',
      state: 'UP',
      stateName: 'Uttar Pradesh',
      city: 'Balrampur',
      deliveryFrom: 'Balrampur, Uttar Pradesh',
      distanceFromBuyer: 450,
      paymentDate: '2025-12-06',
      liftingDate: '2025-12-12',
      grade: 'M-30',
      season: '2024-25',
      packSize: '50 kg bags',
      quantity: 750,
      rate: 37800,
      trucksAvailable: 8,
      insuranceAvailable: true,
      transportationRatePerTon: 1500
    },
    {
      id: 3,
      date: '2025-11-29',
      offerId: 'SLO2025003',
      product: 'White Refined Sugar',
      millName: 'Triveni Engineering',
      millCode: 'MILL003',
      state: 'UP',
      stateName: 'Uttar Pradesh',
      city: 'Muzaffarnagar',
      deliveryFrom: 'Muzaffarnagar, Uttar Pradesh',
      distanceFromBuyer: 520,
      paymentDate: '2025-12-07',
      liftingDate: '2025-12-13',
      grade: 'S-31',
      season: '2024-25',
      packSize: '50 kg bags',
      quantity: 600,
      rate: 39200,
      trucksAvailable: 15,
      insuranceAvailable: true,
      transportationRatePerTon: 1400
    },
    {
      id: 4,
      date: '2025-11-30',
      offerId: 'SLO2025004',
      product: 'White Refined Sugar',
      millName: 'Dalmia Bharat Sugar',
      millCode: 'MILL004',
      state: 'KA',
      stateName: 'Karnataka',
      city: 'Mandya',
      deliveryFrom: 'Mandya, Karnataka',
      distanceFromBuyer: 780,
      paymentDate: '2025-12-08',
      liftingDate: '2025-12-14',
      grade: 'M-30',
      season: '2024-25',
      packSize: '50 kg bags',
      quantity: 450,
      rate: 38900,
      trucksAvailable: 10,
      insuranceAvailable: true,
      transportationRatePerTon: 1800
    },
    {
      id: 5,
      date: '2025-11-30',
      offerId: 'SLO2025005',
      product: 'White Refined Sugar',
      millName: 'Dwarikesh Sugar Industries',
      millCode: 'MILL005',
      state: 'UP',
      stateName: 'Uttar Pradesh',
      city: 'Bijnor',
      deliveryFrom: 'Bijnor, Uttar Pradesh',
      distanceFromBuyer: 320,
      paymentDate: '2025-12-09',
      liftingDate: '2025-12-15',
      grade: 'L-30',
      season: '2024-25',
      packSize: '50 kg bags',
      quantity: 800,
      rate: 37500,
      trucksAvailable: 20,
      insuranceAvailable: true,
      transportationRatePerTon: 1300
    }
  ];

  const [filteredLoads, setFilteredLoads] = useState(mockLoads);

  // Update available mills when state filter changes
  useEffect(() => {
    if (filters?.state === 'all') {
      const uniqueMills = [...new Set(mockLoads?.map(load => load?.millCode))]
        ?.map(code => {
          const load = mockLoads?.find(l => l?.millCode === code);
          return { value: code, label: load?.millName };
        });
      setAvailableMills([{ value: 'all', label: 'All Mills' }, ...uniqueMills]);
    } else {
      const stateMills = mockLoads
        ?.filter(load => load?.state === filters?.state)
        ?.reduce((acc, load) => {
          if (!acc?.some(m => m?.value === load?.millCode)) {
            acc?.push({ value: load?.millCode, label: load?.millName });
          }
          return acc;
        }, []);
      setAvailableMills([{ value: 'all', label: 'All Mills' }, ...stateMills]);
      
      if (filters?.mill !== 'all' && !stateMills?.some(m => m?.value === filters?.mill)) {
        setFilters(prev => ({ ...prev, mill: 'all' }));
      }
    }
  }, [filters?.state]);

  useEffect(() => {
    let result = [...mockLoads];

    if (filters?.state !== 'all') {
      result = result?.filter(load => load?.state === filters?.state);
    }

    if (filters?.mill !== 'all') {
      result = result?.filter(load => load?.millCode === filters?.mill);
    }

    if (filters?.grade !== 'all') {
      result = result?.filter(load => load?.grade === filters?.grade);
    }

    if (filters?.distanceSlab !== 'all') {
      result = result?.filter(load => {
        const distance = load?.distanceFromBuyer;
        switch (filters?.distanceSlab) {
          case '<300':
            return distance < 300;
          case '300-600':
            return distance >= 300 && distance <= 600;
          case '600-1000':
            return distance > 600 && distance <= 1000;
          case '>1000':
            return distance > 1000;
          default:
            return true;
        }
      });
    }

    if (filters?.search) {
      const searchLower = filters?.search?.toLowerCase();
      result = result?.filter(load => 
        load?.millName?.toLowerCase()?.includes(searchLower) ||
        load?.millCode?.toLowerCase()?.includes(searchLower) ||
        load?.product?.toLowerCase()?.includes(searchLower) ||
        load?.offerId?.toLowerCase()?.includes(searchLower) ||
        load?.city?.toLowerCase()?.includes(searchLower) ||
        load?.stateName?.toLowerCase()?.includes(searchLower)
      );
    }

    if (filters?.minPrice) {
      result = result?.filter(load => load?.rate >= parseFloat(filters?.minPrice));
    }

    if (filters?.maxPrice) {
      result = result?.filter(load => load?.rate <= parseFloat(filters?.maxPrice));
    }

    result?.sort((a, b) => {
      let aValue = a?.[sortConfig?.key];
      let bValue = b?.[sortConfig?.key];

      if (sortConfig?.key === 'date' || sortConfig?.key === 'paymentDate' || sortConfig?.key === 'liftingDate') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (aValue < bValue) {
        return sortConfig?.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig?.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    setFilteredLoads(result);
  }, [filters, sortConfig]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleResetFilters = () => {
    setFilters({
      state: 'all',
      mill: 'all',
      grade: 'all',
      distanceSlab: 'all',
      search: '',
      minPrice: '',
      maxPrice: ''
    });
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleBookSugarOnly = (purchaseData) => {
    setSugarBookingModal({ isOpen: true, data: purchaseData });
  };

  const handleConfirmSugarBooking = () => {
    setSugarBookingModal({ isOpen: false, data: null });
    console.log('Proceeding to payment gateway for sugar booking');
  };

  const handleBookBundle = (load) => {
    const noTrucksAvailable = load?.trucksAvailable === 0;
    setBundleBookingModal({ isOpen: true, data: load, noTrucks: noTrucksAvailable });
  };

  const handleConfirmBundleBooking = () => {
    setBundleBookingModal({ isOpen: false, data: null, noTrucks: false });
    console.log('Proceeding to payment gateway for bundle booking');
  };

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader />
      <main className="main-content">
        <BreadcrumbNavigation />
        
        <div className="max-w-[1920px] mx-auto px-6 py-8">
          <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
            <Icon name="Info" size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-semibold text-blue-900 mb-1">Factory Buyer View</h3>
              <p className="text-sm text-blue-700">
                This is how buyers see the marketplace. You can browse available sugar loads and understand the buyer experience.
              </p>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Sugar Marketplace - Buyer View</h1>
              <p className="text-muted-foreground">
                Preview how buyers browse and book your sugar loads
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="default"
                iconName="RefreshCw"
                iconPosition="left"
                onClick={() => window.location?.reload()}
              >
                Refresh
              </Button>
              <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
                <button
                  onClick={() => setViewMode('table')}
                  className={`p-2 rounded-md transition-colors ${
                    viewMode === 'table' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                  }`}
                  aria-label="Table view"
                >
                  <Icon name="Table" size={20} />
                </button>
                <button
                  onClick={() => setViewMode('cards')}
                  className={`p-2 rounded-md transition-colors ${
                    viewMode === 'cards' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                  }`}
                  aria-label="Card view"
                >
                  <Icon name="LayoutGrid" size={20} />
                </button>
              </div>
            </div>
          </div>

          <FilterPanel
            filters={filters}
            onFilterChange={handleFilterChange}
            onReset={handleResetFilters}
            resultsCount={filteredLoads?.length}
            availableMills={availableMills}
          />

          {filteredLoads?.length === 0 ? (
            <div className="bg-card border border-border rounded-lg p-12 text-center">
              <Icon name="PackageX" size={64} className="mx-auto text-muted-foreground mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-2">No loads found</h3>
              <p className="text-muted-foreground mb-6">
                Try adjusting your filters or search criteria to find available sugar loads
              </p>
              <Button
                variant="outline"
                iconName="RotateCcw"
                iconPosition="left"
                onClick={handleResetFilters}
              >
                Reset All Filters
              </Button>
            </div>
          ) : (
            <>
              {viewMode === 'table' ? (
                <div className="hidden lg:block">
                  <MarketplaceTable
                    loads={filteredLoads}
                    onBookSugarOnly={handleBookSugarOnly}
                    onBookBundle={handleBookBundle}
                    onSort={handleSort}
                    sortConfig={sortConfig}
                  />
                </div>
              ) : null}
              
              {viewMode === 'cards' || viewMode === 'table' ? (
                <div className={viewMode === 'table' ? 'lg:hidden' : ''}>
                  <MarketplaceCards
                    loads={filteredLoads}
                    onBookSugarOnly={handleBookSugarOnly}
                    onBookBundle={handleBookBundle}
                  />
                </div>
              ) : null}
            </>
          )}

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Truck" size={24} className="text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground">Truck Marketplace</h3>
                  <p className="text-sm text-muted-foreground">Find verified transporters</p>
                </div>
              </div>
              <Button
                variant="outline"
                iconName="ArrowRight"
                iconPosition="right"
                onClick={() => navigate('/truck-marketplace')}
                fullWidth
              >
                Browse Trucks
              </Button>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Users" size={24} className="text-secondary" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground">Transporter Management</h3>
                  <p className="text-sm text-muted-foreground">Manage your transporters & vehicles</p>
                </div>
              </div>
              <Button
                variant="outline"
                iconName="ArrowRight"
                iconPosition="right"
                onClick={() => navigate('/factory-transporter-management')}
                fullWidth
              >
                Manage Transporters
              </Button>
            </div>
          </div>
        </div>
      </main>

      {/* Modals */}
      <SugarBookingModal
        isOpen={sugarBookingModal?.isOpen}
        onClose={() => setSugarBookingModal({ isOpen: false, data: null })}
        purchaseData={sugarBookingModal?.data}
        onConfirm={handleConfirmSugarBooking}
        quotaStatus="available"
      />
      <BundleBookingModal
        isOpen={bundleBookingModal?.isOpen}
        onClose={() => setBundleBookingModal({ isOpen: false, data: null, noTrucks: false })}
        load={bundleBookingModal?.data}
        noTrucksAvailable={bundleBookingModal?.noTrucks}
        onConfirm={handleConfirmBundleBooking}
      />
    </div>
  );
};

export default FactoryBuyerMarketplaceView;